import { useEffect, useRef, useState } from "react";
import { ReactElement } from "react";

import { SvgIconProps } from "@mui/material";

export type SettingsSubNavItem = {
  id: string;
  label: string;
  icon: ReactElement<SvgIconProps>;
};

const SMALL_OFFSET = 8; // Small spacing to avoid being too close to top

const scrollToId = (id: string): void => {
  const el = document.getElementById(id);
  if (!el) return;

  // Set scroll margin to account for fixed header, then scroll
  const topNavHeight = getComputedStyle(document.documentElement)
    .getPropertyValue("--top-nav-height")
    .trim();
  el.style.scrollMarginTop = `calc(${topNavHeight} + ${SMALL_OFFSET}px)`;

  el.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

export const useSettingsSubNavState = (items: SettingsSubNavItem[]) => {
  const [activeId, setActiveId] = useState<string>(() => items[0]?.id ?? "");
  const isProgrammaticScroll = useRef(false);
  const programmaticTimer = useRef<number | null>(null);

  // Scroll-spy: track which section heading is closest to the top.
  useEffect(() => {
    if (items.length === 0) return;

    const elements = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;

        // Find the entry with the topmost intersecting position.
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.id;
          setActiveId((prev) => {
            if (prev !== id) {
              if (window.history.replaceState) {
                window.history.replaceState(null, "", `#${id}`);
              }
            }
            return id;
          });
        }
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  // On mount, honor an existing hash by scrolling to it.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    if (!items.some((item) => item.id === hash)) return;
    // Defer slightly so sections have rendered.
    const t = window.setTimeout(() => {
      isProgrammaticScroll.current = true;
      setActiveId(hash);
      scrollToId(hash);
      window.setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 600);
    }, 50);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (id: string): void => {
    if (programmaticTimer.current) {
      window.clearTimeout(programmaticTimer.current);
    }
    isProgrammaticScroll.current = true;
    setActiveId(id);
    if (window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    }
    scrollToId(id);
    programmaticTimer.current = window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 700);
  };

  return { activeId, handleSelect };
};
